/* eslint-disable max-lines-per-function */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import type { PetitionDetailsPageData } from 'src/app/pages/petitionGamma/details/pageContext';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useCurrentUserIdAsync } from 'src/app/shared/hooks/session';
import { useMappedLoadedState } from 'src/app/shared/hooks/state';
import { isLoaded } from 'src/app/shared/utils/async';

import { shareVisualsProcessing } from '../../shared';
import { useShareVisuals } from '../useShareVisuals';

type Petition = Pick<PetitionDetailsPageData['petition'], 'id' | 'user' | 'slug' | 'shareVisuals'>;
type Context = 'petition_page';
type UseShareVisualContentsParams = { petition: Petition; context: Context };

export type ShareVisual = {
	id: string;
	variantName: string;
	image: { alt: string; srcSet: string; src: string; fullsizeSrc: string };
};
export type UseShareVisualContentsData = {
	disabled: boolean;
	shareVisuals: ShareVisual[];
	shareVisualsState: 'loading' | 'loaded' | 'timeout';
	shareVisualDownloaded: boolean;
};
type UseShareVisualContentsActions = {
	onShareVisualDownload: (event: React.FormEvent<HTMLFormElement>) => void;
	onShareVisualSelect: () => void;
};
type UseShareVisualContentsReturn = ModelHookResult<UseShareVisualContentsData, UseShareVisualContentsActions>;

export function useShareVisualContents({
	petition,
	context,
}: UseShareVisualContentsParams): UseShareVisualContentsReturn {
	const { translate } = useI18n();

	const shareVisualsQueryState = useShareVisuals({ petition });
	const shareVisualsMappedState = useMappedLoadedState(shareVisualsQueryState, ({ data }) => ({ value: data }));
	const shareVisualsValue = useMemo(
		() => (isLoaded(shareVisualsMappedState) ? shareVisualsMappedState.value : []),
		[shareVisualsMappedState],
	);

	const shareVisualsState = (() => {
		if (shareVisualsValue.length > 0 && !shareVisualsProcessing(shareVisualsValue)) return 'loaded';
		if (shareVisualsQueryState.isTimeout) return 'timeout';
		return 'loading';
	})();

	const shareVisuals = useMemo(() => {
		return shareVisualsValue.map((shareVisual) => {
			const previewLowDPI = shareVisual.versions.find((version) => version.name.includes('1x'));
			const previewHighDPI = shareVisual.versions.find((version) => version.name.includes('2x'));

			return {
				id: shareVisual.id,
				variantName: shareVisual.variationName,
				image: {
					alt: translate(`corgi.petition_gamma.details.share_visuals.${shareVisual.variationName}_alt_text`),
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					src: previewLowDPI!.url,
					srcSet: `${previewLowDPI?.url} 1x, ${previewHighDPI?.url} 2x`,
					fullsizeSrc: shareVisual.url,
				},
			};
		});
	}, [shareVisualsValue, translate]);

	const [disabled, setDisabled] = useState(true);
	const onShareVisualSelect = () => {
		setDisabled(false);
	};

	const navigate = useNavigate();
	const track = useTracking();
	const currentUserIdState = useCurrentUserIdAsync();
	const currentUserId = isLoaded(currentUserIdState) ? currentUserIdState.value : undefined;
	const sourceLocation = useQueryParamValue('source_location');
	const [shareVisualDownloaded, setShareVisualDownloaded] = useState(false);
	const onShareVisualDownload = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const selectedShareVisual = new FormData(event.target as HTMLFormElement).get('share-visuals');
			const shareVisual = shareVisuals.find((visual) => visual.variantName === selectedShareVisual);

			void track('petition_share_content_download', {
				view_context: 'petition_show',
				own_petition: petition.user.id === currentUserId,
				context,
				content: shareVisual?.variantName,
				petition_id: `petition_${petition.id}`,
				source_location: sourceLocation,
			});

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			navigate(appendQueryParam(shareVisual!.image.fullsizeSrc, 'download', 'true'));
			setShareVisualDownloaded(true);
		},
		[context, currentUserId, navigate, petition.id, petition.user.id, shareVisuals, sourceLocation, track],
	);

	useEffect(() => {
		const timeout = window.setTimeout(() => {
			if (shareVisualDownloaded) setShareVisualDownloaded(false);
		}, 5000);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [shareVisualDownloaded]);

	return {
		data: { disabled, shareVisuals, shareVisualsState, shareVisualDownloaded },
		actions: { onShareVisualDownload, onShareVisualSelect },
	};
}

function appendQueryParam(url: string, param: string, paramValue: string) {
	const urlObj = new URL(url);
	urlObj.searchParams.append(param, paramValue);
	return urlObj.toString();
}
